import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Clock, Navigation } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { shopInfo } from '../data/menuData';

const ContactSection = () => {
  const { language, t } = useLanguage();

  const contactInfo = [
    {
      icon: MapPin,
      label: t('contact.address'),
      value: shopInfo.address,
      action: shopInfo.googleMapsUrl,
      actionLabel: t('contact.directions')
    },
    {
      icon: Phone,
      label: t('contact.phone'),
      value: shopInfo.phone,
      action: `tel:${shopInfo.phone.replace(/\s/g, '')}`,
      actionLabel: t('contact.callUs')
    },
    {
      icon: Clock,
      label: t('contact.hours'),
      value: `${shopInfo.hours.days[language]}: ${shopInfo.hours.open} - ${shopInfo.hours.close}`,
      action: null,
      actionLabel: null
    }
  ];

  return (
    <section id="contact" className="py-20 bg-[#1A1816]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-[#FDFBF7] mb-4">
            {t('contact.title')}
          </h2>
          <p className="font-manrope text-lg text-[#FDFBF7]/70 mb-6">
            {t('contact.subtitle')}
          </p>
          <div className="w-24 h-1 bg-[#9F3E1F] mx-auto rounded-full" />
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#FDFBF7]/5 backdrop-blur rounded-2xl p-6 border border-[#FDFBF7]/10"
                data-testid={`contact-info-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#9F3E1F] rounded-full flex items-center justify-center flex-shrink-0">
                    <info.icon size={24} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-manrope text-[#FDFBF7]/60 text-sm mb-1">{info.label}</p>
                    <p className="font-manrope text-[#FDFBF7] font-semibold text-lg">{info.value}</p>
                    {info.action && (
                      <motion.a
                        href={info.action}
                        target={info.action.startsWith('http') ? '_blank' : undefined}
                        rel={info.action.startsWith('http') ? 'noopener noreferrer' : undefined}
                        className="inline-flex items-center gap-2 text-[#9F3E1F] font-medium mt-2 hover:text-[#D4AF37] transition-colors"
                        whileHover={{ x: 5 }}
                      >
                        <span>{info.actionLabel}</span>
                        <Navigation size={16} />
                      </motion.a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}

            {/* Quick Actions - Only Call Button */}
            <div className="pt-4">
              <motion.a
                href={`tel:${shopInfo.phone.replace(/\s/g, '')}`}
                className="w-full flex items-center justify-center gap-3 bg-[#9F3E1F] text-white px-6 py-4 rounded-full font-manrope font-semibold hover:bg-[#8A351A] transition-colors"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                data-testid="contact-call-btn"
              >
                <Phone size={20} />
                {t('contact.callUs')}: {shopInfo.phone}
              </motion.a>
            </div>

            {/* Features */}
            <div className="flex flex-wrap gap-3 pt-4">
              {shopInfo.features.map((feature, index) => (
                <span
                  key={index}
                  className="bg-[#FDFBF7]/10 text-[#FDFBF7] px-4 py-2 rounded-full font-manrope text-sm"
                >
                  {feature}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="h-[400px] lg:h-[500px] rounded-2xl overflow-hidden shadow-2xl"
            data-testid="contact-map"
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2446.3!2d4.6558!3d52.1341!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c5c5f5f5f5f5f5%3A0x0!2sHooftstraat%2078%2C%202406%20GL%20Alphen%20aan%20den%20Rijn!5e0!3m2!1sen!2snl!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="AK-AL Location"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
